import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { toDate } from 'date-fns-tz';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { StagedUser, SkippedUser, ProcessedUser, CrossAccountWarning } from '../services/HxcStagingService';
import { logger } from 'utils/logger';

const log = logger.getLogger('ProcessStagingDialog');

interface ProcessStagingDialogProps {
  open: boolean;
  onClose: (isClosed: boolean) => void;
  selectedUsers: StagedUser[];
  onProcess: (userIds: number[], onboardDate?: string, allowMigrate?: boolean) => Promise<{ processedUsers: ProcessedUser[]; skippedUsers: SkippedUser[] }>;
  crossAccountWarnings?: Map<string, CrossAccountWarning>;
}

type DialogState = 'confirmation' | 'processing' | 'results';

const ProcessStagingDialog: React.FC<ProcessStagingDialogProps> = ({
  open,
  onClose,
  selectedUsers,
  onProcess,
  crossAccountWarnings
}) => {
  const [dialogState, setDialogState] = useState<DialogState>('confirmation');
  const [showUserList, setShowUserList] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showLongWaitMessage, setShowLongWaitMessage] = useState(false);
  const [processedUsers, setProcessedUsers] = useState<ProcessedUser[]>([]);
  const [skippedUsers, setSkippedUsers] = useState<SkippedUser[]>([]);
  const [onboardDate, setOnboardDate] = useState<Date | null>(null);
  const [isAsap, setIsAsap] = useState(false); // Default to not ASAP (date picker enabled)
  const [allowMigrate, setAllowMigrate] = useState(false);

  // Group users by account name and SFDC ID
  const groupedUsers = selectedUsers.reduce((acc, user) => {
    const key = `${user.accountName}|${user.sfdcAccountId}`;
    if (!acc[key]) {
      acc[key] = {
        accountName: user.accountName,
        sfdcAccountId: user.sfdcAccountId,
        users: []
      };
    }
    acc[key].users.push(user);
    return acc;
  }, {} as Record<string, { accountName: string; sfdcAccountId: string; users: StagedUser[] }>);

  const groupedArray = Object.values(groupedUsers);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setDialogState('confirmation');
      setShowUserList(false);
      setProcessing(false);
      setShowLongWaitMessage(false);
      setProcessedUsers([]);
      setSkippedUsers([]);
      setOnboardDate(null);
      setIsAsap(false);
      setAllowMigrate(false);
    }
  }, [open]);

  // Handle 30-second wait message
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (dialogState === 'processing' && processing) {
      timer = setTimeout(() => {
        setShowLongWaitMessage(true);
      }, 30000); // 30 seconds
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [dialogState, processing]);

  const handleCancel = () => {
    onClose(true);
  }

  const handleEscape = () => {
    if (dialogState !== 'processing') {
      onClose(true);
    }
  }

  const handleClose = () => {
    onClose(false);
  };

  const handleProcess = async () => {
    try {
      setDialogState('processing');
      setProcessing(true);
      setShowLongWaitMessage(false);

      // Extract user IDs as numbers
      const userIds = selectedUsers.map(user => parseInt(user.id, 10));
      
      // Format onboard date if ASAP is not checked and a date is selected
      let formattedDate: string | undefined = undefined;
      if (!isAsap && onboardDate) {
        // Build date string for midnight Hawaii time
        const year = onboardDate.getFullYear();
        const month = String(onboardDate.getMonth() + 1).padStart(2, '0');
        const day = String(onboardDate.getDate()).padStart(2, '0');
        const hawaiiDateString = `${year}-${month}-${day} 00:00:00`;
        
        // Interpret this string AS Hawaii time and convert to Date object
        const date = toDate(hawaiiDateString, { timeZone: 'Pacific/Honolulu' });
        
        // Get UTC ISO string and remove milliseconds
        formattedDate = date.toISOString().slice(0, 19) + 'Z';
      }
      
      log.log(`Processing ${userIds.length} users with onboard date:`, formattedDate || 'ASAP', 'allowMigrate:', allowMigrate);

      const result = await onProcess(userIds, formattedDate, allowMigrate);
      
      setProcessedUsers(result.processedUsers);
      setSkippedUsers(result.skippedUsers);
      setDialogState('results');
      
      if (result.skippedUsers.length === 0) {
        log.log('All users processed successfully');
      } else {
        log.warn(`${result.skippedUsers.length} users were skipped`);
      }
    } catch (error) {
      log.error('Error processing users:', error);
      // Keep in processing state to show error? Or should we handle errors differently?
    } finally {
      setProcessing(false);
    }
  };

  const toggleUserList = () => {
    setShowUserList(!showUserList);
  };

  // Extract unique accounts from processed users
  const getUniqueAccounts = () => {
    const accountsMap = new Map<number, { accountName: string; accountId: number }>();
    
    processedUsers.forEach(user => {
      if (!accountsMap.has(user.accountId)) {
        accountsMap.set(user.accountId, {
          accountName: user.accountName,
          accountId: user.accountId
        });
      }
    });
    
    return Array.from(accountsMap.values());
  };

  return (
    <Dialog
      open={open}
      onClose={handleEscape}
      maxWidth="md"
      fullWidth
    >
      {dialogState === 'confirmation' && (
        <>
          <DialogTitle>Process Staging Users</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to process <strong>{selectedUsers.length}</strong> user{selectedUsers.length !== 1 ? 's' : ''}?
            </Typography>

            {selectedUsers.length >= 10 && (
              <Typography variant="body2" color="warning.main" sx={{ mt: 2, mb: 2 }}>
                ⚠️ Processing {selectedUsers.length} users may take a few minutes to complete.
              </Typography>
            )}

            <Box sx={{ mt: 3, mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Onboarding Date"
                  value={onboardDate}
                  onChange={(newValue) => setOnboardDate(newValue)}
                  disabled={isAsap}
                  minDate={new Date(new Date().setDate(new Date().getDate() + 1))}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: false,
                      sx: { width: 200 }
                    }
                  }}
                />
              </LocalizationProvider>
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAsap}
                    onChange={(e) => setIsAsap(e.target.checked)}
                  />
                }
                label="ASAP"
              />
            </Box>

            <Box sx={{ mt: 1, mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allowMigrate}
                    onChange={(e) => setAllowMigrate(e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" component="span" fontWeight="medium">
                      Allow Account Migration
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      If a user is a member of an existing Account, they will be moved to the one listed below.<br/>
                      If the same User is listed more than once, they will end up in the Account of the last entry listed.
                    </Typography>
                  </Box>
                }
                sx={{ mt: 1, alignItems: 'flex-start' }}
              />
            </Box>

            <Box sx={{ mt: 2, mb: 2 }}>
              <Button
                onClick={toggleUserList}
                endIcon={showUserList ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                size="small"
              >
                {showUserList ? 'Hide' : 'Show'} User List
              </Button>
            </Box>

            <Collapse in={showUserList}>
              <TableContainer 
                component={Paper} 
                sx={{ maxHeight: 400, overflow: 'auto' }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Account Name</TableCell>
                      <TableCell>SFDC Account ID</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Email</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupedArray.map((group, groupIndex) => (
                      <React.Fragment key={groupIndex}>
                        {group.users.map((user, userIndex) => {
                          const warning = crossAccountWarnings?.get(user.id);
                          const warningTooltip = warning
                            ? allowMigrate
                              ? `This user will be moved from "${warning.existingAccountName}" (ID: ${warning.existingAccountId})`
                              : `This user is already a member of "${warning.existingAccountName}" (ID: ${warning.existingAccountId})`
                            : null;
                          return (
                          <TableRow key={user.id}>
                            {userIndex === 0 && (
                              <>
                                <TableCell rowSpan={group.users.length}>
                                  <strong>{group.accountName}</strong>
                                </TableCell>
                                <TableCell rowSpan={group.users.length}>
                                  <strong>{group.sfdcAccountId}</strong>
                                </TableCell>
                              </>
                            )}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {user.firstName} {user.lastName}
                                {warningTooltip && (
                                  <Tooltip title={warningTooltip}>
                                    <WarningAmberIcon fontSize="small" color="warning" sx={{ flexShrink: 0 }} />
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>{user.email}</TableCell>
                          </TableRow>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button onClick={handleProcess} variant="contained" color="primary">
              Process
            </Button>
          </DialogActions>
        </>
      )}

      {dialogState === 'processing' && (
        <>
          <DialogTitle>Processing Users</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress size={60} />
              <Typography variant="body1" sx={{ mt: 3 }}>
                Processing {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''}...
              </Typography>
              {showLongWaitMessage && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                  This is taking longer than expected. You can return in a minute or two and it should be completed.
                </Typography>
              )}
            </Box>
          </DialogContent>
        </>
      )}

      {dialogState === 'results' && (
        <>
          <DialogTitle>
            {skippedUsers.length === 0 ? 'Processing Complete' : 'Processing Complete with Warnings'}
          </DialogTitle>
          <DialogContent>
            {skippedUsers.length === 0 ? (
              <Box sx={{ py: 2 }}>
                <Typography variant="body1" color="success.main" gutterBottom>
                  ✓ Successfully processed {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''}!
                </Typography>
                
                {/* Affiliate Accounts Table */}
                {processedUsers.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Affiliate Accounts
                    </Typography>
                    <TableContainer 
                      component={Paper} 
                      sx={{ maxHeight: 300, overflow: 'auto' }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Account Name</strong></TableCell>
                            <TableCell><strong>Account ID</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {getUniqueAccounts().map((account) => (
                            <TableRow key={account.accountId}>
                              <TableCell>{account.accountName}</TableCell>
                              <TableCell>{account.accountId}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            ) : (
              <Box>
                <Typography variant="body1" gutterBottom>
                  Processed {selectedUsers.length - skippedUsers.length} of {selectedUsers.length} users successfully.
                </Typography>
                
                {/* Affiliate Accounts Table */}
                {processedUsers.length > 0 && (
                  <Box sx={{ mt: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Affiliate Accounts
                    </Typography>
                    <TableContainer 
                      component={Paper} 
                      sx={{ maxHeight: 300, overflow: 'auto', mb: 3 }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Account Name</strong></TableCell>
                            <TableCell><strong>Account ID</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {getUniqueAccounts().map((account) => (
                            <TableRow key={account.accountId}>
                              <TableCell>{account.accountName}</TableCell>
                              <TableCell>{account.accountId}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
                
                <Typography variant="body2" color="warning.main" sx={{ mt: 1, mb: 2 }}>
                  ⚠️ {skippedUsers.length} user{skippedUsers.length !== 1 ? 's were' : ' was'} skipped:
                </Typography>

                <TableContainer 
                  component={Paper} 
                  sx={{ maxHeight: 400, overflow: 'auto' }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Account Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {skippedUsers.map((user, index) => (
                        <TableRow key={index}>
                          <TableCell>{user.accountName}</TableCell>
                          <TableCell>{user.userEmail}</TableCell>
                          <TableCell>{user.reason}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleClose} variant="contained">
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default ProcessStagingDialog;
